import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Music, Volume2, Users, Headphones } from 'lucide-react';

const HowToUseADUAsBackyardMusicLessonRoom = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const ctaButtons = [
    {
      icon: Calendar,
      text: "Book & Schedule",
      onClick: () => setShowCalendly(true)
    },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];


  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-backyard-music-lesson-room" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU for Music Lessons or Student Practice Studio
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            This ADU layout is perfect for teachers of piano, voice, guitar, or strings. Great for solo rehearsal, Zoom teaching, or in-person training.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Sound-Safe Space Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Give Students the Sound-Safe Space They Deserve</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Music className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Piano, voice, guitar, or strings</span>
                </div>
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Solo rehearsal space</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">In-person and virtual teaching</span>
                </div>
                <div className="flex items-center gap-3">
                  <Headphones className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Professional audio setup</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&h=400" 
                alt="Music Lesson Room ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Acoustic Considerations Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Acoustic Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Soundproofing</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Acoustic paneling or foam on walls</li>
                <li>• Soundproofing around doors and floors</li>
                <li>• Double-pane windows for noise control</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Layout Design</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Dual-use layout with seating + instrument zone</li>
                <li>• Proper ventilation for comfort</li>
                <li>• Storage for music equipment and sheet music</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Benefits Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Instructor Benefits</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Volume2 className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Noise Control</h3>
              <p className="text-muted-foreground">Avoids home noise + neighbors complaints</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Home className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Separate Space</h3>
              <p className="text-muted-foreground">Separates teaching from personal space</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Professional Image</h3>
              <p className="text-muted-foreground">Professional vibe helps with branding</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Build Your Music Teaching Studio
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Create a professional space where music education can flourish without limitations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsBackyardMusicLessonRoom;